/* This is the ColorPicker or "Crayon Box" component for picking colors.
For examples on how to use this, see storybook.
*/
// dependencies
import { nanoid } from "nanoid/non-secure";
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
// hooks
import { useOnClickOutside } from "usehooks-ts";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// utils
import { Hex, chooseBestContrastingColor, hexToRGB } from "../../utils/getContrast";

// components
import Alert from "../Alert";
import Text from "../Text";
// styles
import css from "./ColorPicker.module.scss";
// config
import { colorCollection } from "./config";

// Distance between 2 colors (in RGB)
// https://stackoverflow.com/questions/23990802/find-nearest-color-from-a-colors-list
const colorDistance = (a, b) => {
	return Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2) + Math.pow(a[2] - b[2], 2));
};

/**
 * Returns the nearest color from the colorCollection
 */
export const getNearestColor = (color: Hex) => {
	const colorCollectionFlat = colorCollection.flat().map((color) => color.toLowerCase());

	let tmp;
	let index = 0;
	let lowest = Infinity;

	colorCollectionFlat.forEach((colorCollectionColor, ii) => {
		tmp = colorDistance(hexToRGB(color), hexToRGB(colorCollectionColor));
		if (tmp < lowest) {
			lowest = tmp;
			index = ii;
		}
	});

	return colorCollectionFlat[index] as Hex;
};

interface ColorPickerProps {
	/** (Optional) The label to display in the component */
	label?: string;
	/** The color Hex for the initial selection. Use getRandomTopShelfColor() to get a random valid color. */
	value: Hex;
	/** A callback function that will be called when a color is selected. The function will be passed the color Hex as a string. */
	onChange: (color: Hex) => void;
	/** "(Optional) An error message to display beside the component." */
	errorMessage?: string;
	/**
	 * (Optional) A callback function that will be called when an invalid
	 * color is selected. This allows the parent component to handle the
	 * error message.
	 */
	onInvalidColor?: (value: Hex) => void;
}

interface CrayonProps {
	color: Hex;
	onClick: (color: string) => void;
	isSelected: boolean;
	inputID: string;
}

const Crayon = (props: CrayonProps) => {
	const { color, onClick, isSelected, inputID } = props;
	const checkColor = useMemo(() => {
		// This logic matches the logic in the Engagement Cohort Chart label display
		return chooseBestContrastingColor({ background: color as Hex, standard: 2.5 }).toString();
	}, [color]);

	return (
		<label
			className={css.colorCheckbox}
			style={{ backgroundColor: color, color: checkColor }}
			htmlFor={`${inputID}_${color}`}
		>
			{isSelected && <FontAwesomeIcon size="sm" icon={faCheck} />}
			{/* Radio Input is a hidden option to provide accessability */}
			<input
				className={css.hiddenAccessabilityRadioButton}
				type="radio"
				name={inputID}
				id={`${inputID}_${color}`}
				value={color}
				onChange={() => onClick(color)}
				checked={isSelected}
			/>
		</label>
	);
};

// the imported "colorCollection" includes all colors in sub arrays by row
// the flattened color collection with all colors at the top level in one array (and lowercased for ease of comparison)
const colorCollectionFlat = colorCollection.flat().map((color) => color.toLowerCase());

export const getRandomTopShelfColor = () => {
	const randomNumber = Math.floor(Math.random() * colorCollection[0].length);
	return colorCollection[0][randomNumber] as Hex;
};

/**
 * Color picker input component
 */
export const ColorPicker = (props: ColorPickerProps) => {
	const { label, errorMessage } = props;

	/**
	 * Calculate the value and if it is a valid color.
	 */
	const { value, isInvalidColor } = useMemo(() => {
		if (!props.value) {
			return {
				isInvalidColor: false,
			};
		}

		if (props.value && colorCollectionFlat.includes(props.value.toLowerCase())) {
			return {
				value: props.value,
				isInvalidColor: false,
			};
		}

		return {
			isInvalidColor: true,
		};
	}, [props.value]);

	/**
	 * If the value is not a valid color, call the onInvalidColor callback
	 * function if it exists. Otherwise, log a warning to the console.
	 */
	useEffect(() => {
		if (!isInvalidColor) return;

		if (props.onInvalidColor) {
			props.onInvalidColor(props.value);
		} else {
			console.warn(`ColorPicker: The value "${props.value}" is not a valid color. Please use a valid color.`);
		}
	}, [isInvalidColor]);

	const mainColorPickerDivRef = useRef<HTMLDivElement>(null);

	const modalInputId = useMemo(() => nanoid(), []);

	const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

	// Open or close the Modal for color selection
	const handleToggleModal = () => {
		setIsOpenModal(!isOpenModal);
	};

	// Close the color selection Modal if the user clicks outside of it
	useOnClickOutside(mainColorPickerDivRef, () => {
		if (isOpenModal) {
			setIsOpenModal(false);
		}
	});

	// Handles a color selection in the color modal
	const handleCrayonClick = (color: Hex) => {
		props.onChange(color);

		// Close the modal
		handleToggleModal();
	};
	const labelColor = errorMessage ? "red-6" : "neutral-9";

	// Opens the modal if it is selected and one presses Space
	const handleKeyDown = (event) => {
		if (event.key === " ") {
			handleToggleModal();
			// Don't page down, we're opening the modal instead.
			event.preventDefault();
		}
	};

	return (
		<div ref={mainColorPickerDivRef} className={css.main}>
			<div className={css.colorBlockLabel}>
				<div
					className={css.selectedColor}
					style={{ backgroundColor: value }}
					onClick={handleToggleModal}
					tabIndex={0}
					onKeyDown={handleKeyDown}
				/>
				<div onClick={handleToggleModal}>
					<Text
						htmlFor={modalInputId}
						element="label"
						color={labelColor}
						weight="semi-bold"
						size="paragraph-2"
					>
						{(label || errorMessage) && `${label}${errorMessage ? "*" : ""}`}
					</Text>
				</div>
				{isOpenModal ? (
					<div className={css.colorSelectionModal}>
						{colorCollection.map((row, index) => {
							return (
								<div key={index} className={css.rowOfCrayons}>
									{row.map((color: Hex) => {
										return (
											<Crayon
												key={color}
												color={color}
												onClick={handleCrayonClick}
												isSelected={color === value ? true : false}
												inputID={modalInputId}
											/>
										);
									})}
								</div>
							);
						})}
					</div>
				) : null}
			</div>
			{errorMessage ? <Alert type="error" message={errorMessage} className={css.errorMessage} /> : null}
		</div>
	);
};

export default ColorPicker;
